import { lazy, Suspense } from 'react'
import { Navigate, Route, Routes } from 'react-router-dom'

import { PortalLayout } from '~/layouts/PortalLayout'
import { TileLoader } from '~/shared/ui/components/TileLoader'

const wrap = (title: string, Component: React.ComponentType) => (
  <PortalLayout title={title}>
    <Suspense fallback={<TileLoader fill size={64} />}>
      <Component />
    </Suspense>
  </PortalLayout>
)

const lazyPage = <T extends Record<string, unknown>>(
  loader: () => Promise<T>,
  exportName: keyof T
) => lazy(async () => {
  const mod = await loader()
  return { default: mod[exportName] as React.ComponentType }
})

const AdminDashboardPage = lazyPage(() => import('~/domains/admin/modules/dashboard/pages/AdminDashboardPage'), 'AdminDashboardPage')
const AdminOrganizationsPage = lazyPage(() => import('~/domains/admin/modules/organizations/pages/AdminOrganizationsPage'), 'AdminOrganizationsPage')
const AdminReportsPage = lazyPage(() => import('~/domains/admin/modules/reports/pages/AdminReportsPage'), 'AdminReportsPage')
const AdminSettingsPage = lazyPage(() => import('~/domains/admin/modules/settings/pages/AdminSettingsPage'), 'AdminSettingsPage')
const AdminSubscriptionsPage = lazyPage(() => import('~/domains/admin/modules/subscriptions/pages/AdminSubscriptionsPage'), 'AdminSubscriptionsPage')
const AdminUsersPage = lazyPage(() => import('~/domains/admin/modules/users/pages/AdminUsersPage'), 'AdminUsersPage')

const ClientDashboardPage = lazyPage(() => import('~/domains/procurement/modules/dashboard/pages/ClientDashboardPage'), 'ClientDashboardPage')
const ClientApprovalsPage = lazyPage(() => import('~/domains/procurement/modules/approvals/pages/ClientApprovalsPage'), 'ClientApprovalsPage')
const ViewClientApprovalPage = lazyPage(() => import('~/domains/procurement/modules/approvals/pages/ViewClientApprovalPage'), 'ViewClientApprovalPage')
const ViewInvoice = lazyPage(() => import('~/domains/billing/modules/billing/ui/ViewInvoice'), 'ViewInvoice')
const ClientDealsPage = lazyPage(() => import('~/domains/procurement/modules/deals/pages/ClientDealsPage'), 'ClientDealsPage')
const ClientOrdersPage = lazyPage(() => import('~/domains/procurement/modules/purchase-orders/pages/ClientOrdersPage'), 'ClientOrdersPage')
const ViewClientOrderPage = lazyPage(() => import('~/domains/procurement/modules/purchase-orders/pages/ViewClientOrderPage'), 'ViewClientOrderPage')
const ClientOrganizationSettingsPage = lazyPage(() => import('~/domains/procurement/modules/organization-settings/pages/ClientOrganizationSettingsPage'), 'ClientOrganizationSettingsPage')
const ProvidersHubPage = lazyPage(() => import('~/domains/procurement/modules/providers/pages/ProvidersHubPage'), 'ProvidersHubPage')
const ViewProviderProfilePage = lazyPage(() => import('~/domains/procurement/modules/providers/pages/ViewProviderProfilePage'), 'ViewProviderProfilePage')
const ClientQuotesPage = lazyPage(() => import('~/domains/procurement/modules/quotations/pages/ClientQuotesPage'), 'ClientQuotesPage')
const ViewClientQuotePage = lazyPage(() => import('~/domains/procurement/modules/quotations/pages/ViewClientQuotePage'), 'ViewClientQuotePage')
const ClientReportsPage = lazyPage(() => import('~/domains/procurement/modules/reports/pages/ClientReportsPage'), 'ClientReportsPage')
const ClientRequestsPage = lazyPage(() => import('~/domains/procurement/modules/requests/pages/ClientRequestsPage'), 'ClientRequestsPage')
const CreateClientRequestPage = lazyPage(() => import('~/domains/procurement/modules/requests/pages/CreateClientRequestPage'), 'CreateClientRequestPage')
const ClientRequestDetailsPage = lazyPage(() => import('~/domains/procurement/modules/requests/pages/ClientRequestDetailsPage'), 'ClientRequestDetailsPage')
const ClientRequestComparePage = lazyPage(() => import('~/domains/procurement/modules/requests/pages/ClientRequestComparePage'), 'ClientRequestComparePage')

const ProviderApprovalsPage = lazyPage(() => import('~/domains/procurement/modules/approvals/pages/ProviderApprovalsPage'), 'ProviderApprovalsPage')
const ProviderClientsPage = lazyPage(() => import('~/domains/procurement/modules/clients/pages/ProviderClientsPage'), 'ProviderClientsPage')
const ViewClientProfilePage = lazyPage(() => import('~/domains/procurement/modules/clients/pages/ViewClientProfilePage'), 'ViewClientProfilePage')
const ProviderDashboardPage = lazyPage(() => import('~/domains/procurement/modules/dashboard/pages/ProviderDashboardPage'), 'ProviderDashboardPage')
const ProviderDealsPage = lazyPage(() => import('~/domains/procurement/modules/deals/pages/ProviderDealsPage'), 'ProviderDealsPage')
const CreateDealPage = lazyPage(() => import('~/domains/procurement/modules/deals/pages/CreateDealPage'), 'CreateDealPage')
const ViewProviderDealPage = lazyPage(() => import('~/domains/procurement/modules/deals/pages/ViewProviderDealPage'), 'ViewProviderDealPage')
const ProviderOrdersPage = lazyPage(() => import('~/domains/procurement/modules/purchase-orders/pages/ProviderOrdersPage'), 'ProviderOrdersPage')
const ViewProviderOrderPage = lazyPage(() => import('~/domains/procurement/modules/purchase-orders/pages/ViewProviderOrderPage'), 'ViewProviderOrderPage')
const ProviderOrganizationSettingsPage = lazyPage(() => import('~/domains/procurement/modules/organization-settings/pages/ProviderOrganizationSettingsPage'), 'ProviderOrganizationSettingsPage')
const ProviderQuotationsPage = lazyPage(() => import('~/domains/procurement/modules/quotations/pages/ProviderQuotationsPage'), 'ProviderQuotationsPage')
const ProviderQuotationDetailPage = lazyPage(() => import('~/domains/procurement/modules/quotations/pages/ProviderQuotationDetailPage'), 'ProviderQuotationDetailPage')
const ProviderQuoteEditPageWrapper = lazyPage(() => import('~/domains/procurement/modules/quotations/pages/ProviderQuoteEditPageWrapper'), 'ProviderQuoteEditPageWrapper')
const ProviderReportsPage = lazyPage(() => import('~/domains/procurement/modules/reports/pages/ProviderReportsPage'), 'ProviderReportsPage')
const ProviderRequestsPage = lazyPage(() => import('~/domains/procurement/modules/requests/pages/ProviderRequestsPage'), 'ProviderRequestsPage')
const ViewProviderRequestPage = lazyPage(() => import('~/domains/procurement/modules/requests/pages/ViewProviderRequestPage'), 'ViewProviderRequestPage')
const ProviderQuoteCreatePageWrapper = lazyPage(() => import('~/domains/procurement/modules/quotations/pages/ProviderQuoteCreatePageWrapper'), 'ProviderQuoteCreatePageWrapper')

const SceneGallery = lazy(() => import('~/shared/ui/crossing/SceneGallery'))
const AuthDebug = lazyPage(() => import('~/components/AuthDebug'), 'AuthDebug')
const AuthCallbackHandler = lazyPage(() => import('~/components/auth/AuthCallbackHandler'), 'AuthCallbackHandler')
const LoginPage = lazy(() => import('~/components/auth/LoginPage'))
const OnboardingPage = lazy(() => import('~/domains/auth/modules/onboarding/ui/OnboardingPage'))
const RootRedirect = lazy(() =>
  import('~/components/auth/RootRedirect').then(m => ({ default: m.RootRedirect }))
)

export function App() {
  return (
    <Routes>
      <Route
        path="/"
        element={
          <Suspense fallback={<TileLoader fill size={64} />}>
            <RootRedirect />
          </Suspense>
        }
      />
      <Route
        path="/onboarding"
        element={
          <Suspense fallback={<TileLoader fill size={64} />}>
            <OnboardingPage />
          </Suspense>
        }
      />

      <Route
        path="/login"
        element={
          <Suspense fallback={<TileLoader fill size={64} />}>
            <LoginPage />
          </Suspense>
        }
      />

      <Route path="/admin/dashboard" element={wrap('Admin Dashboard', AdminDashboardPage)} />
      <Route path="/admin/organizations" element={wrap('Organization Management', AdminOrganizationsPage)} />
      <Route path="/admin/reports" element={wrap('Admin Reports', AdminReportsPage)} />
      <Route path="/admin/settings" element={wrap('Admin Settings', AdminSettingsPage)} />
      <Route path="/admin/subscriptions" element={wrap('Subscriptions', AdminSubscriptionsPage)} />
      <Route path="/admin/users" element={wrap('Users', AdminUsersPage)} />

      <Route path="/client/dashboard" element={wrap('Client Dashboard', ClientDashboardPage)} />
      <Route path="/client/approvals" element={wrap('Approvals', ClientApprovalsPage)} />
      <Route path="/client/approvals/:id" element={wrap('Approval', ViewClientApprovalPage)} />
      <Route path="/client/billing/invoices/:id" element={wrap('Invoice', ViewInvoice)} />
      <Route path="/client/deals" element={wrap('Deals', ClientDealsPage)} />
      <Route path="/client/orders" element={wrap('Orders', ClientOrdersPage)} />
      <Route path="/client/orders/:id" element={wrap('Order', ViewClientOrderPage)} />
      <Route path="/client/organization-settings" element={wrap('Organization Settings', ClientOrganizationSettingsPage)} />
      <Route path="/client/providers" element={wrap('Providers', ProvidersHubPage)} />
      <Route path="/client/providers/:id" element={wrap('Provider', ViewProviderProfilePage)} />
      <Route path="/client/quotes" element={wrap('Quotes', ClientQuotesPage)} />
      <Route path="/client/quotes/:id" element={wrap('Quote', ViewClientQuotePage)} />
      <Route path="/client/reports" element={wrap('Reports', ClientReportsPage)} />
      <Route path="/client/requests" element={wrap('Requests', ClientRequestsPage)} />
      <Route path="/client/requests/create" element={wrap('Create Request', CreateClientRequestPage)} />
      <Route path="/client/requests/:id" element={wrap('Request', ClientRequestDetailsPage)} />
      <Route path="/client/requests/:id/compare" element={wrap('Compare Quotes', ClientRequestComparePage)} />

      <Route path="/client/accounts" element={<Navigate to="/client/providers" replace />} />
      <Route path="/client/suppliers" element={<Navigate to="/client/providers" replace />} />
      <Route path="/client/suppliers/:id" element={<NavigateProviders />} />

      <Route path="/provider/approvals" element={wrap('Provider Approvals', ProviderApprovalsPage)} />
      <Route path="/provider/billing/invoices/:id" element={wrap('Invoice', ViewInvoice)} />
      <Route path="/provider/clients" element={wrap('Clients', ProviderClientsPage)} />
      <Route path="/provider/clients/:id" element={wrap('Client', ViewClientProfilePage)} />
      <Route path="/provider/dashboard" element={wrap('Provider Dashboard', ProviderDashboardPage)} />
      <Route path="/provider/deals" element={wrap('Deals', ProviderDealsPage)} />
      <Route path="/provider/deals/create" element={wrap('Create Deal', CreateDealPage)} />
      <Route path="/provider/deals/:id" element={wrap('Deal', ViewProviderDealPage)} />
      <Route path="/provider/orders" element={wrap('Orders', ProviderOrdersPage)} />
      <Route path="/provider/orders/:id" element={wrap('Order', ViewProviderOrderPage)} />
      <Route path="/provider/organization-settings" element={wrap('Organization Settings', ProviderOrganizationSettingsPage)} />
      <Route path="/provider/quotations" element={wrap('Quotations', ProviderQuotationsPage)} />
      <Route path="/provider/quotations/:id" element={wrap('Quotation', ProviderQuotationDetailPage)} />
      <Route path="/provider/quotations/:id/edit" element={wrap('Edit Quotation', ProviderQuoteEditPageWrapper)} />
      <Route path="/provider/reports" element={wrap('Provider Reports', ProviderReportsPage)} />
      <Route path="/provider/requests" element={wrap('Requests', ProviderRequestsPage)} />
      <Route path="/provider/requests/:id" element={wrap('Request', ViewProviderRequestPage)} />
      <Route path="/provider/requests/:id/quote" element={wrap('Submit Quote', ProviderQuoteCreatePageWrapper)} />

      <Route path="/provider/accounts" element={<Navigate to="/provider/clients" replace />} />
      <Route path="/provider/quotes" element={<Navigate to="/provider/quotations" replace />} />
      <Route path="/provider/quotes/:id" element={<NavigateQuotations />} />
      <Route path="/provider/quotes/:id/edit" element={<NavigateQuotationsEdit />} />
      <Route path="/provider/quotes/create" element={<Navigate to="/provider/requests" replace />} />
      <Route path="/provider/quotations/new" element={<Navigate to="/provider/requests" replace />} />

      {import.meta.env.DEV && (
        <Route path="/dev/scenes" element={<Suspense fallback={null}><SceneGallery /></Suspense>} />
      )}
      <Route path="/auth-debug" element={wrap('Auth Debug', AuthDebug)} />
      <Route path="/auth/callback" element={wrap('Anmeldung...', AuthCallbackHandler)} />

      <Route path="*" element={<NotFound />} />
    </Routes>
  )
}

function NotFound() {
  return (
    <PortalLayout title="Error 404">
      <section className="flex items-center h-full p-16">
        <div className="container flex flex-col items-center justify-center px-5 mx-auto my-8">
          <div className="max-w-md text-center">
            <h2 className="mb-8 font-bold text-9xl">
              <span className="sr-only">Error</span>
              <span className="text-primary">404</span>
            </h2>
            <a href="/" className="btn ml-4">Back home</a>
          </div>
        </div>
      </section>
    </PortalLayout>
  )
}

function NavigateProviders() {
  const id = window.location.pathname.split('/').pop()
  return <Navigate to={`/client/providers/${id ?? ''}`} replace />
}

function NavigateQuotations() {
  const id = window.location.pathname.split('/').pop()
  return <Navigate to={`/provider/quotations/${id ?? ''}`} replace />
}

function NavigateQuotationsEdit() {
  const parts = window.location.pathname.split('/')
  const id = parts[parts.length - 2]
  return <Navigate to={`/provider/quotations/${id ?? ''}/edit`} replace />
}
